import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/learn-panel.css';
import APIService from '../../services/APIService';
import { renderMarkdown } from './markdown';
import { IconX } from './icons';

/*
 * LearnPanel — the Knowledge-Hub "learn" page that docks beside a practice
 * mode: Module chip, concept name, intro, and tabbed pointer notes (the tabs
 * are the concept's NODES). Content comes from GET /hub/learn/{id}, which
 * walks up the concept tree so an unlabeled leaf shows its nearest ancestor's
 * page rather than nothing.
 */
const cache = new Map();

const LearnPanel = ({ conceptId, conceptOptions = [], onPick, onClose }) => {
  const [data, setData] = useState(() => cache.get(conceptId) || null);
  const [tabId, setTabId] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!conceptId) return undefined;
    setErr('');
    if (cache.has(conceptId)) { setData(cache.get(conceptId)); setTabId(null); return undefined; }
    let alive = true;
    setData(null);
    APIService.getHubLearn(conceptId)
      .then((d) => { cache.set(conceptId, d); if (alive) { setData(d); setTabId(null); } })
      .catch((e) => { if (alive) { setErr(e.message || 'Could not load.'); setData({}); } });
    return () => { alive = false; };
  }, [conceptId]);

  const doc = data?.doc;
  const tabs = useMemo(() => doc?.tabs || [], [doc]);
  const tab = useMemo(() => tabs.find((t) => t.id === tabId) || tabs[0], [tabs, tabId]);

  return (
    <aside className="lpn-wrap" aria-label="Learn">
      <div className="lpn-head">
        {conceptOptions.length > 1 ? (
          <select className="lpn-pick" value={conceptId || ''} onChange={(e) => onPick?.(e.target.value)}>
            {conceptOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : (
          <span className="lpn-chip">{data?.parent_name || 'Learn'}</span>
        )}
        <button className="lpn-close" onClick={onClose} type="button" aria-label="Close learn panel">
          <IconX size={15} />
        </button>
      </div>

      {data === null ? (
        <div className="lpn-empty"><div className="lpn-spinner" /> Loading…</div>
      ) : err ? (
        <div className="lpn-empty">{err}</div>
      ) : !doc ? (
        <div className="lpn-empty">
          <b>{data?.node?.name}</b>
          <p>No learn notes for this concept yet — add them in the Knowledge Hub, and they'll appear here.</p>
        </div>
      ) : (
        <div className="lpn-body">
          {data.parent_name && conceptOptions.length > 1 && <span className="lpn-chip">{data.parent_name}</span>}
          <h1 className="lpn-title">{data.node?.name}</h1>
          {data.inherited_from && (
            <div className="lpn-inherit">Showing notes from <b>{data.inherited_from}</b> — the closest concept with a learn page.</div>
          )}
          {doc.intro && <p className="lpn-intro">{doc.intro}</p>}

          {tabs.length > 0 && (
            <>
              <div className="lpn-tabs" role="tablist">
                {tabs.map((t) => (
                  <button key={t.id} type="button" role="tab" aria-selected={t.id === (tab?.id)}
                    className={t.id === tab?.id ? 'on' : ''} onClick={() => setTabId(t.id)}>
                    {t.label}
                  </button>
                ))}
              </div>
              {tab?.md ? (
                <div className="lpn-md">{renderMarkdown(tab.md)}</div>
              ) : (
                <ul className="lpn-points">
                  {(tab?.points || []).map((p, i) => (typeof p === 'string' ? (
                    <li key={i}><span className="lpn-dot" />{p}</li>
                  ) : (
                    <li key={i}><span className="lpn-dot" /><span><b>{p.t}</b> — {p.d}</span></li>
                  )))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </aside>
  );
};

/* The pill that toggles the panel — shared by every mode. */
export const LearnToggle = ({ open, onToggle }) => (
  <button type="button" className={`lpn-toggle${open ? ' on' : ''}`} onClick={onToggle}
    title={open ? 'Hide the learn page' : 'Read the concept notes beside your practice'}>
    📖 Learn
  </button>
);

/* Per-mode persisted open state. */
export const useLearnOpen = (modeKey) => {
  const [open, setOpen] = useState(() => localStorage.getItem(`dv_learn_${modeKey}`) === 'open');
  const toggle = () => setOpen((o) => {
    localStorage.setItem(`dv_learn_${modeKey}`, o ? 'closed' : 'open');
    return !o;
  });
  return [open, toggle];
};

export default LearnPanel;
